/**
 * sessionStorage cache hook for curriculum data - FIXED
 * Invalidates on: logout, clear cache, close tab (automatic)
 */
import { useCallback, useEffect, useState } from 'react'
import { CACHE_KEY_PREFIX, CACHE_TTL_MS } from '../constants'
import { ISkillsStandards } from 'pages/NewProject/components/ProjectSkillsStandards/interfaces'

const getKey = (gradeId: string) => `${CACHE_KEY_PREFIX}${gradeId}`

const isExpired = (timestamp: number): boolean =>
  Date.now() - timestamp > CACHE_TTL_MS

/** Get cached curriculum data from sessionStorage */
export const getCachedCurriculum = (gradeId: string): ISkillsStandards | null => {
  try {
    const key = getKey(gradeId)
    const cached = sessionStorage.getItem(key)
    if (!cached) return null

    const { data, timestamp } = JSON.parse(cached)
    if (isExpired(timestamp)) {
      sessionStorage.removeItem(key)
      return null
    }

    // Basic shape validation
    if (!data || !data.curriculumItems) {
      sessionStorage.removeItem(key)
      return null
    }

    return data as ISkillsStandards
  } catch {
    return null
  }
}

/** Set curriculum data in sessionStorage cache */
export const setCachedCurriculum = (gradeId: string, data: ISkillsStandards): void => {
  try {
    const key = getKey(gradeId)
    const entry = { data, timestamp: Date.now() }
    sessionStorage.setItem(key, JSON.stringify(entry))
  } catch {
    // Silently fail if storage is full
  }
}

/** Clear all curriculum cache entries */
export const clearCurriculumCache = (): void => {
  try {
    const keys = Object.keys(sessionStorage)
    for (const key of keys) {
      if (key.startsWith(CACHE_KEY_PREFIX)) {
        sessionStorage.removeItem(key)
      }
    }
  } catch {
    // Silently fail
  }
}

/** Hook for curriculum cache management - uses state for reactive updates */
export const useCurriculumCache = (gradeId: string) => {
  const [cached, setCached] = useState<ISkillsStandards | null>(null)

  useEffect(() => {
    setCached(getCachedCurriculum(gradeId))
  }, [gradeId])

  const getCache = useCallback(() => getCachedCurriculum(gradeId), [gradeId])

  const setCache = useCallback((data: ISkillsStandards) => {
    setCachedCurriculum(gradeId, data)
    setCached(data)
  }, [gradeId])

  const clearCache = useCallback(() => {
    clearCurriculumCache()
    setCached(null)
  }, [])

  return { getCache, setCache, clearCache, cached }
}
